import { CardType } from '../../Prototype/Level/Card/CardType';
import { ArrayHelper } from '../../Utility/ArrayHelper';
import { getCardSuit, Suit } from '../../Utility/CardsHelper';
import { IGameState } from '../Game/IGameState';
import { MissionType } from '../Player/IMission';
import { updateMissionProgress } from '../Player/PlayerMissionHelper';
import { setStateTaskData } from '../Player/PlayerStateHelper';
import { PlayerTask } from '../Player/PlayerTasks';
import { queuePopup } from '../UI/UIStateHelper';
import { ILevelStateTaskData, LevelStatus, LevelType } from './ILevelState';
import { isExpiredFlag, setFlag, getFlag } from '../Player/PlayerFlagHelper';
import { APP_RATE_REMINDER, APP_RATE_SUBMITTED } from '../../Constants/Flags';
import { APP_RATE_REMINDER_COOLDOWN } from '../../Constants/Timers';
import { APP_RATE_REMINDER_MIN_LEVEL } from '../../Constants/RateOurApp';

const SUIT_MISSION_MAPPING = {
    [Suit.CLUBS]: MissionType.CLEAR_CARD_CLUBS,
    [Suit.HEARTS]: MissionType.CLEAR_CARD_HEARTS,
    [Suit.SPADES]: MissionType.CLEAR_CARD_SPADES,
    [Suit.DIAMONDS]: MissionType.CLEAR_CARD_DIAMONDS,
};

export function getDefaultBoosterPrices() {
    return {
        bullsEye: 250,
        joker: 300,
        plusFive: 200,
        shuffle: 150,
        tnt: 650,
        undo: 50,
    };
}

export default class LevelStateHelper {
    public static updateMissionProgress(
        state: IGameState,
        previousState: IGameState
    ): IGameState {
        // Get "cleared" cards (folded and discarded).
        const clearedCards = [].concat(
            ArrayHelper.difference(
                state.levelState.cardsState.folded,
                previousState.levelState.cardsState.folded
            ),
            ArrayHelper.difference(
                state.levelState.cardsState.discarded,
                previousState.levelState.cardsState.discarded
            )
        );

        // Update clear cards missions progress.
        for (const clearedCard of clearedCards) {
            const cardData = state.levelState.cardsState.cards[clearedCard];
            if (
                cardData.type === CardType.value &&
                ArrayHelper.includes(
                    previousState.levelState.cardsState.inPlay,
                    clearedCard
                )
            ) {
                state = {
                    ...state,
                    playerState: updateMissionProgress(
                        state.playerState,
                        SUIT_MISSION_MAPPING[getCardSuit(cardData.value)],
                        1
                    ),
                };
            } else if (cardData.type === CardType.joker) {
                state = {
                    ...state,
                    playerState: updateMissionProgress(
                        state.playerState,
                        MissionType.USE_JOKER,
                        1
                    ),
                };
            }
        }

        // Update streak missions progress.
        if (
            state.levelState.cardsState.streakSequence > 0 &&
            state.levelState.cardsState.streakSequence % 5 === 0
        ) {
            state = {
                ...state,
                playerState: updateMissionProgress(
                    state.playerState,
                    MissionType.STREAKS,
                    1
                ),
            };
        }

        return state;
    }

    public static levelComplete(state: IGameState): IGameState {
        // Calculate bonus cards' points. For each card remaining in the stack add 5 points plus another 5
        // points for each card.
        const increment = 5;
        const inStack = state.levelState.cardsState.inStack.length;
        const inPlay = state.levelState.cardsState.inPlay.length;
        const count = inStack + inPlay;
        const bonus = (increment / 2) * count * (count + 1);
        const score = state.levelState.score + bonus;
        const stars = state.levelState.stars.filter(n => n <= score).length;

        let coins = 0;
        if (state.levelState.firstAttempt) {
            // Player only earns coins the first time.
            coins = state.levelState.coins[stars];
            state = {
                ...state,
                playerState: setStateTaskData(state.playerState, {
                    task: PlayerTask.LEVEL_NODE_COMPLETE,
                }),
            };

            // First time opening app after the "lost progress" update
            const isInstalledFromUpdate = state.platform.isInstallFromUpdate;
            if (isInstalledFromUpdate && state.levelState.levelNumber === 1) {
                coins += 3000;
            }
        }

        state = {
            ...state,
            levelState: {
                ...state.levelState,
                score: score,
                bonus: bonus,
                status: LevelStatus.COMPLETE,
                completeData: {
                    levelNumber: state.levelState.levelNumber,
                    score: score,
                    stars: stars,
                    coins: coins,
                },
            },
        };

        if (state.levelState.levelType === LevelType.SAGA) {
            state = LevelStateHelper.check3StarReward(state);
            //state = LevelStateHelper.checkRateApp(state);
        }

        return state;
    }

    public static check3StarReward(state: IGameState): IGameState {
        if (
            state.levelState.firstAttempt &&
            state.levelState.completeData.stars === 3 &&
            state.levelState.levelNumber > 3
        ) {
            return {
                ...state,
                UIState: queuePopup(state.UIState, 'threeStarRewardMenu', {
                    levelNumber: state.levelState.levelNumber,
                }),
            };
        }
        return state;
    }

    public static checkRateApp(state: IGameState): IGameState {
        if (
            // For new players we prompt it as soon as they beat level 9.
            state.levelState.levelNumber >= APP_RATE_REMINDER_MIN_LEVEL && // prettier-ignore
            // If they hit "Submit" then we don't show it again.
            !getFlag(state.playerState, APP_RATE_SUBMITTED) &&
            // For existing players we should have a cool down of 7 days if they hit "Later".
            isExpiredFlag(
                state.playerState,
                APP_RATE_REMINDER,
                APP_RATE_REMINDER_COOLDOWN
            )
        ) {
            return {
                ...state,
                UIState: queuePopup(state.UIState, 'rateAppMenu', null),
                playerState: setFlag(state.playerState, APP_RATE_REMINDER),
            };
        }
        return state;
    }

    public static setStateTaskData(
        state: IGameState,
        taskData: ILevelStateTaskData
    ): IGameState {
        return {
            ...state,
            levelState: {
                ...state.levelState,
                tasks: [...state.levelState.tasks, taskData],
            },
        };
    }
}

export function isEndGame(state: IGameState) {
    return (
        typeof state.levelState !== 'undefined' &&
        typeof state.levelState.cardsState !== 'undefined' &&
        state.levelState.cardsState.inStack.length === 0 &&
        state.levelState.cardsState.inPlay.length > 0
    );
}
